function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function cloneJsonValue(value) {
  if (value === undefined) {
    return undefined;
  }
  return JSON.parse(JSON.stringify(value));
}

function normalizeTraceSelector(selector) {
  const traceId =
    selector?.trace_id === '' || selector?.trace_id == null
      ? null
      : String(selector.trace_id).trim();
  const rawWavelength = selector?.wavelength_nm;

  if (rawWavelength === '' || rawWavelength == null) {
    return { trace_id: traceId, wavelength_nm: null };
  }

  const wavelength = Number(rawWavelength);
  return {
    trace_id: traceId,
    wavelength_nm: Number.isFinite(wavelength) ? wavelength : rawWavelength,
  };
}

export function getSavedRunIdentity(runDetail) {
  return isPlainObject(runDetail?.identity) ? runDetail.identity : null;
}

export function getSavedRunPipeline(runDetail) {
  return getSavedRunIdentity(runDetail)?.pipeline || null;
}

export function getSavedRunProjectSample(runDetail) {
  return isPlainObject(runDetail?.project_sample) ? runDetail.project_sample : null;
}

export function getSavedRunSampleLabel(runDetail) {
  const sample = getSavedRunProjectSample(runDetail);
  return sample?.display_name || sample?.sample_id || '—';
}

export function getSavedRunLineageSummary(runDetail) {
  return isPlainObject(runDetail?.lineage_summary) ? runDetail.lineage_summary : null;
}

function getSavedRunLineageFlag(runDetail, fieldName) {
  const lineage = getSavedRunLineageSummary(runDetail);
  if (!lineage || !Object.prototype.hasOwnProperty.call(lineage, fieldName)) {
    return null;
  }
  const value = lineage[fieldName];
  if (value == null) {
    return null;
  }
  return Boolean(value);
}

export function getSavedRunRelationshipType(runDetail) {
  return getSavedRunLineageSummary(runDetail)?.relationship_type || null;
}

export function getSavedRunSourceRunId(runDetail) {
  return getSavedRunLineageSummary(runDetail)?.source_run_id || null;
}

export function getSavedRunRerunReasonCode(runDetail) {
  return getSavedRunLineageSummary(runDetail)?.rerun_reason_code || '';
}

export function getSavedRunRerunReasonText(runDetail) {
  return getSavedRunLineageSummary(runDetail)?.rerun_reason_text || '';
}

export function getSavedRunParamsChanged(runDetail) {
  return getSavedRunLineageFlag(runDetail, 'params_changed');
}

export function getSavedRunTraceChanged(runDetail) {
  return getSavedRunLineageFlag(runDetail, 'trace_changed');
}

export function getSavedRunMeasurementInputs(runDetail) {
  return Array.isArray(runDetail?.measurement_inputs)
    ? runDetail.measurement_inputs.filter(isPlainObject)
    : [];
}

export function getSavedRunParams(runDetail) {
  const params = runDetail?.frozen_inputs?.params;
  return isPlainObject(params) ? cloneJsonValue(params) : null;
}

export function getSavedRunRequestedTraces(runDetail) {
  return getSavedRunMeasurementInputs(runDetail).map((input) => {
    const requestedTrace = normalizeTraceSelector(input.requested_trace || {});
    return {
      import_id: input.import_id,
      trace_id: requestedTrace.trace_id,
      wavelength_nm: requestedTrace.wavelength_nm,
      filename: input?.file?.original_filename || null,
      file_role: input?.file_role || null,
    };
  });
}

export function getSavedRunEffectiveTraces(runDetail) {
  return getSavedRunMeasurementInputs(runDetail).map((input) => {
    const effectiveTrace = normalizeTraceSelector(input.effective_trace || {});
    return {
      import_id: input.import_id,
      trace_id: effectiveTrace.trace_id,
      wavelength_nm: effectiveTrace.wavelength_nm,
      time_unit_raw: input?.effective_trace?.time_unit_raw || null,
      signal_unit_raw: input?.effective_trace?.signal_unit_raw || null,
      signal_unit_family: input?.effective_trace?.signal_unit_family || null,
      signal_unit_canonical: input?.effective_trace?.signal_unit_canonical || null,
      source_format: input?.effective_trace?.source_format || null,
      filename: input?.file?.original_filename || null,
      file_role: input?.file_role || null,
    };
  });
}

export function getSavedRunExecutionSetup(runDetail) {
  return isPlainObject(runDetail?.execution_setup) ? runDetail.execution_setup : null;
}

export function getSavedRunOfficialResultSummary(runDetail) {
  return isPlainObject(runDetail?.official_result_summary)
    ? runDetail.official_result_summary
    : null;
}

export function getSavedRunOfficialSummary(runDetail) {
  const summary = getSavedRunOfficialResultSummary(runDetail)?.summary;
  return isPlainObject(summary) ? summary : null;
}

export function getSavedRunArtifacts(runDetail) {
  return isPlainObject(runDetail?.artifacts) ? runDetail.artifacts : {};
}

export function getSavedRunFailureMessage(runDetail) {
  return runDetail?.failure?.message || null;
}

export function getSavedRunOperationalState(runDetail) {
  return isPlainObject(runDetail?.operational_state) ? runDetail.operational_state : null;
}

export function getSavedRunStatus(runDetail) {
  return getSavedRunOperationalState(runDetail)?.status || '—';
}

export function getSavedRunValidity(runDetail) {
  const isValid = getSavedRunOperationalState(runDetail)?.is_valid;
  if (isValid === true) return 'Valid';
  if (isValid === false) return 'Invalid';
  return '—';
}

export function formatSavedRunChangeSummary(runDetail) {
  const paramsChanged = getSavedRunParamsChanged(runDetail);
  const traceChanged = getSavedRunTraceChanged(runDetail);
  if (paramsChanged == null || traceChanged == null) {
    return 'Frozen execution diff not recorded.';
  }
  return `Params ${paramsChanged ? 'changed' : 'same'} • Trace ${
    traceChanged ? 'changed' : 'same'
  }`;
}

export function getSavedRunIdentitySummary(runDetail) {
  const relationshipType = getSavedRunRelationshipType(runDetail);
  const sourceRunId = getSavedRunSourceRunId(runDetail);

  if (relationshipType === 'standalone_new_run') {
    return 'Original saved run for this batch sample.';
  }

  if (relationshipType === 'exact_rerun') {
    return sourceRunId
      ? `Exact rerun of source run ${sourceRunId}. Inputs, pairing, params, and trace were replayed unchanged.`
      : 'Exact rerun using the frozen execution snapshot.';
  }

  if (relationshipType === 'derived_new_run') {
    return sourceRunId
      ? `Derived from source run ${sourceRunId}. Inputs and pairing stayed fixed while params and/or requested traces changed.`
      : 'Derived run using the same frozen inputs and pairing.';
  }

  return relationshipType || 'Frozen lineage summary not recorded.';
}
